import {
  Box,
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import {
  useAllTransportActivities,
  useNewRoadsRoadImprovements,
  useRoadMaintenance,
} from '@src/modules/performance/context/reports-api-hooks';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import StackedAreaChart from '@src/modules/performance/components/charts/StackedAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Activity Class Expenditure',
  content: [
    {
      title: null,
      description:
  <Text fontSize="inherit" mb={2}>
    Achieved expenditure by
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/planning-and-investment-knowledge-base/202427-nltp-investment-requirements/202427-nltp-activity-classes-and-work-categories" isExternal>Work Category</Link>
    {' '}
    is reported annually by Approved Organisations into Transport Investment Online (TIO).
    The reported expenditure is for co-invested activities only and is separated by NZTA (NZTA share) and the Approved Organisation (local share).
    From the reported expenditure, it can be seen how much NZTA and Territorial Authorities jointly spend on transport, including infrastructure, transport planning, road maintenance, walking and cycling, and passenger transport.
  </Text>,
      source: null,
    },
    {
      title: 'All transport activities',
      description:
  <Text fontSize="inherit" mb={2}>
    All transport-related expenditure co-invested by NLTF (NZTA share) and Approved Organisations (local share). Expenditure has been grouped into the activity classes:
    <br />
    • Road Maintenance - Road Maintenance;
    <br />
    • Walking and Cycling - Walking & cycling;
    <br />
    • Road Improvement - Road Improvements;
    <br />
    • Other - Freight, Investment management and transport planning, promotion, education and demand management, public transport, and road policing.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, major activities expenditure</Link>
  </Text>,
    },
    {
      title: 'New roads & road improvements',
      description:
  <Text fontSize="inherit" mb={2}>
    The expenditure on Road Improvements including bridge and structures replacement, minor improvements (low cost, low risk), new roads, property purchase, road improvements, seal extension, and traffic management facilities. Expenditure has been grouped into:
    <br />
    • Minor Improvements - Minor Improvements (low cost, low risk);
    <br />
    • New Roads - New Roads;
    <br />
    • Road Improvements - Road improvements, Seal extension;
    <br />
    • Bridges and Structures - Bridges & structures replacement;
    <br />
    • Other - Professional Services, Traffic Management
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, new and improved infrastructure expenditure</Link>
  </Text>,
    },
    {
      title: 'Road maintenance, operations and renewals',
      description:
  <Text fontSize="inherit" mb={2}>
    The expenditure on maintenance, operations and renewals associated with the pavement and surfacing, footpaths, structures, cycling facilities, environment and drainage, cyclic corridor maintenance and emergency reinstatement. Expenditure has been grouped into:
    <br />
    • Corridor = Traffic services maintenance, Operational traffic management, Level crossing warning devices;
    <br />
    • Pavement = Pavement maintenance
    <br />
    • Surfacing = Sealed road resurfacing, Unsealed road metalling;
    <br />
    • Emergency = Emergency works;
    <br />
    • Environment and Drainage = Environmental maintenance, and Drainage maintenance;
    <br />
    • Footpaths, Cycleways, and Structures - Footpath maintenance, Cycle path maintenance, and Structures maintenance.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, operation and renewals expenditure</Link>
  </Text>,
    },
  ],
};

function TransportProgrammeDeliveryActivityClassExpenditure() {
  const { data: allTransportActivities, isLoading: isLoadingAllTransportActivities } = useAllTransportActivities();
  const { data: newRoadsRoadImprovements, isLoading: isLoadingNewRoadsRoadImprovements } = useNewRoadsRoadImprovements();
  const { data: roadMaintenance, isLoading: isLoadingRoadMaintenance } = useRoadMaintenance();
  const isLoading = isLoadingAllTransportActivities || isLoadingNewRoadsRoadImprovements || isLoadingRoadMaintenance;

  // Check if all lockYears are the same
  const allLockYears = [
    allTransportActivities?.[0]?.LockYear,
    newRoadsRoadImprovements?.[0]?.LockYear,
    roadMaintenance?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // All Transport Activities
  const allTransportActivitiesYears = [...new Set(allTransportActivities?.map((item) => item.Year))];
  const allTransportActivitiesChartData = [
    {
      name: 'Road Maintenance',
      type: 'area',
      data: allTransportActivitiesYears.map((year) => {
        const items = allTransportActivities?.filter((item) => item.Year === year && item.Type2 === 1);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transblue,
    },
    {
      name: 'Walking & Cycling',
      type: 'area',
      data: allTransportActivitiesYears.map((year) => {
        const items = allTransportActivities?.filter((item) => item.Year === year && item.Type2 === 2);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transgrey,
    },
    {
      name: 'Road Improvement',
      type: 'area',
      data: allTransportActivitiesYears.map((year) => {
        const items = allTransportActivities?.filter((item) => item.Year === year && item.Type2 === 3);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transred,
    },
    {
      name: 'Other',
      type: 'area',
      data: allTransportActivitiesYears.map((year) => {
        const items = allTransportActivities?.filter((item) => item.Year === year && item.Type2 === 4);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transgreen,
    },
  ];

  // New Roads & Road Improvements
  const newRoadsRoadImprovementsYears = [...new Set(newRoadsRoadImprovements?.map((item) => item.Year))];
  const newRoadsRoadImprovementsChartData = [
    {
      name: 'Minor Improvements',
      type: 'area',
      data: newRoadsRoadImprovementsYears.map((year) => {
        const items = newRoadsRoadImprovements?.filter((item) => item.Year === year && item.Type2 === 1);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transblue,
    },
    {
      name: 'New Roads',
      type: 'area',
      data: newRoadsRoadImprovementsYears.map((year) => {
        const items = newRoadsRoadImprovements?.filter((item) => item.Year === year && item.Type2 === 2);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transgrey,
    },
    {
      name: 'Road Improvements',
      type: 'area',
      data: newRoadsRoadImprovementsYears.map((year) => {
        const items = newRoadsRoadImprovements?.filter((item) => item.Year === year && item.Type2 === 3);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transred,
    },
    {
      name: 'Bridges & Structures',
      type: 'area',
      data: newRoadsRoadImprovementsYears.map((year) => {
        const items = newRoadsRoadImprovements?.filter((item) => item.Year === year && item.Type2 === 4);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transgreen,
    },
    {
      name: 'Other',
      type: 'area',
      data: newRoadsRoadImprovementsYears.map((year) => {
        const items = newRoadsRoadImprovements?.filter((item) => item.Year === year && item.Type2 === 5);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transorange,
    },
  ];

  // Road maintenance, operations and renewals
  const roadMaintenanceYears = [...new Set(roadMaintenance?.map((item) => item.Year))];
  const roadMaintenanceChartData = [
    {
      name: 'Corridor',
      type: 'area',
      data: roadMaintenanceYears.map((year) => {
        const items = roadMaintenance?.filter((item) => item.Year === year && item.Type2 === 1);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transblue,
    },
    {
      name: 'Pavement',
      type: 'area',
      data: roadMaintenanceYears.map((year) => {
        const items = roadMaintenance?.filter((item) => item.Year === year && item.Type2 === 2);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transgrey,
    },
    {
      name: 'Surfacing',
      type: 'area',
      data: roadMaintenanceYears.map((year) => {
        const items = roadMaintenance?.filter((item) => item.Year === year && item.Type2 === 3);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transred,
    },
    {
      name: 'Emergency',
      type: 'area',
      data: roadMaintenanceYears.map((year) => {
        const items = roadMaintenance?.filter((item) => item.Year === year && item.Type2 === 4);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transgreen,
    },
    {
      name: 'Environment & Drainage',
      type: 'area',
      data: roadMaintenanceYears.map((year) => {
        const items = roadMaintenance?.filter((item) => item.Year === year && item.Type2 === 5);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transorange,
    },
    {
      name: 'Footpaths, Cycleways, Structures',
      type: 'area',
      data: roadMaintenanceYears.map((year) => {
        const items = roadMaintenance?.filter((item) => item.Year === year && item.Type2 === 6);
        const total = items.reduce((acc, item) => acc + item.Value, 0);
        return {
          x: year,
          y: total / 1000000,
        };
      }),
      color: CHART_COLOURS.transpurple,
    },
  ];

  return (
    <ReportSection
      title="Activity Class Expenditure"
      informationSource={informationSource}
      links={[
        {
          label: 'Maintenance Operations & Renewal',
          url: '/performance/maintenance-operations',
          isExternal: false,
          userRoles: ['All'],
        },
      ]}
      isLoading={isLoading || !roadMaintenance || !newRoadsRoadImprovements || !allTransportActivities}
      lockYear={primaryLockYear || null}
    >
      <Wrap>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={allTransportActivities?.[0]?.LockYear}
          />
          )}
          <StackedAreaChart
            title={['All transport activities', '\u00A0']}
            categories={allTransportActivitiesYears}
            series={allTransportActivitiesChartData}
            width="280"
            height="280"
            prefix="$"
            suffix="M"
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={newRoadsRoadImprovements?.[0]?.LockYear}
          />
          )}
          <StackedAreaChart
            title={['New roads & road improvements', '\u00A0']}
            categories={newRoadsRoadImprovementsYears}
            series={newRoadsRoadImprovementsChartData}
            width="280"
            height="280"
            prefix="$"
            suffix="M"
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={roadMaintenance?.[0]?.LockYear}
          />
          )}
          <StackedAreaChart
            title={['Road maintenance, operations', 'and renewals']}
            categories={roadMaintenanceYears}
            series={roadMaintenanceChartData}
            width="280"
            height="280"
            prefix="$"
            suffix="M"
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default TransportProgrammeDeliveryActivityClassExpenditure;
